<template>
  <v-card class="room-selection-card" elevation="2">

    <v-card-text class="pa-6">
      <!-- Selection Controls with Modern Design -->
      <v-row class="selection-controls mb-6">
        <v-col cols="12" sm="5">
          <v-select
              v-model="selectedBlock"
              :items="blocks"
              label="Select Block"
              prepend-inner-icon="mdi-office-building"
              item-text="name"
              return-object
              filled
              rounded
              hide-details="auto"
              class="elevation-2"
              :menu-props="{ offsetY: true }"
          >
            <template v-slot:selection="{ item }">
              <v-chip color="primary" label small class="mr-2">Block</v-chip>
              {{ item.name }}
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" sm="5">
          <v-select
              v-model="selectedFloor"
              :items="floors"
              label="Select Floor"
              prepend-inner-icon="mdi-elevator"
              item-text="name"
              return-object
              filled
              rounded
              hide-details="auto"
              class="elevation-2"
              :disabled="!selectedBlock"
              :menu-props="{ offsetY: true }"
          >
            <template v-slot:selection="{ item }">
              <v-chip color="secondary" label small class="mr-2">Floor</v-chip>
              {{ item.name }}
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" sm="2">
          <v-btn
              block
              color="primary"
              class="px-4 elevation-2"
              height="56"
              :loading="loading"
              @click="fetchRooms"
              :disabled="!selectedFloor"
          >
            <v-icon left>mdi-refresh</v-icon>
            Refresh
          </v-btn>
        </v-col>
      </v-row>

      <!-- Interactive Floor Plan -->
      <v-sheet
          class="floor-plan rounded-lg pa-6"
          elevation="3"
      >
        <div class="d-flex flex-wrap justify-space-around">
          <v-hover v-for="room in availableRooms" :key="room.id" v-slot="{ hover }">
            <v-card
                :class="[
                'room-card',
                'ma-3',
                { 'selected': selectedRoom === room.id },
                { 'elevated': hover }
              ]"
                :color="getRoomColor(room)"
                @click="selectRoom(room)"
                width="150"
                :elevation="hover ? 8 : 2"
                :disabled="!room.available"
            >
              <div class="room-status-indicator" :class="getRoomStatusClass(room)"></div>
              <v-card-title class="justify-center py-2 white--text">
                {{ room.number }}
              </v-card-title>
              <v-card-text class="text-center white--text pa-2">
                <v-icon small color="white" class="mr-1">mdi-bed</v-icon>
                {{ room.bedsAvailable }}/{{ room.totalBeds }}
                <div class="caption font-weight-medium mt-1">{{ room.type }}</div>
              </v-card-text>

              <!-- Enhanced Bed Selection Dialog -->
              <v-dialog v-model="room.showBedDialog" max-width="500">
                <v-card class="bed-selection-dialog">
                  <v-card-title class="headline primary white--text">
                    <v-icon color="white" class="mr-2">mdi-bed</v-icon>
                    Select Bed - Room {{ room.number }}
                  </v-card-title>
                  <v-card-text class="pa-6">
                    <v-row>
                      <v-col v-for="bed in room.beds" :key="bed.number" cols="6" sm="4">
                        <v-hover v-slot="{ hover }">
                          <v-card
                              :class="[
                              'bed-card pa-3',
                              { 'bed-selected': selectedBed?.number === bed.number }
                            ]"
                              :elevation="hover ? 8 : 2"
                              :color="bed.available ? 'white' : 'grey lighten-2'"
                              @click="selectBed(room, bed)"
                              :disabled="!bed.available"
                          >
                            <div class="text-center">
                              <v-icon
                                  size="36"
                                  :color="bed.available ? 'primary' : 'grey'"
                              >
                                mdi-bed
                              </v-icon>
                              <div class="mt-2 font-weight-medium">
                                 {{ bed.name }}
                              </div>
                              <v-chip
                                  x-small
                                  :color="bed.available ? 'success' : 'error'"
                                  class="mt-2"
                                  label
                              >
                                {{ bed.available ? 'Available' : 'Occupied' }}
                              </v-chip>
                            </div>
                          </v-card>
                        </v-hover>
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-divider></v-divider>
                  <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn
                        text
                        class="text-none"
                        @click="room.showBedDialog = false"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                        color="primary"
                        class="text-none"
                        :disabled="!selectedBed"
                        @click="confirmBedSelection(room)"
                    >
                      Confirm Selection
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-card>
          </v-hover>
        </div>
      </v-sheet>

      <!-- Modern Legend -->
      <v-card class="legend-card mt-6" outlined>
        <v-card-text class="py-2">
          <div class="d-flex flex-wrap justify-space-around align-center">
            <v-chip
                v-for="(status, index) in roomStatus"
                :key="index"
                :color="status.color"
                class="ma-2"
                label
                outlined
            >
              <v-icon left small>{{ status.icon }}</v-icon>
              {{ status.text }}
            </v-chip>
          </div>
        </v-card-text>
      </v-card>

      <!-- Enhanced Room Details -->
      <v-expand-transition>
        <v-card
            v-if="selectedRoomDetails"
            class="selected-room-details mt-6"
            elevation="3"
        >
          <v-card-title class="primary white--text">
            <v-icon color="white" class="mr-2">mdi-information</v-icon>
            Selected Room Details
          </v-card-title>
          <v-card-text class="pa-4">
            <v-row>
              <v-col cols="12" sm="6" v-for="detail in roomDetails" :key="detail.label">
                <v-list-item dense>
                  <v-list-item-icon>
                    <v-icon color="primary">{{ detail.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-subtitle class="text--secondary">
                      {{ detail.label }}
                    </v-list-item-subtitle>
                    <v-list-item-title class="font-weight-medium">
                      {{ detail.value }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn
                color="error"
                text
                class="text-none"
                @click="resetSelection"
            >
              <v-icon left>mdi-close</v-icon>
              Reset
            </v-btn>
            <v-btn
                color="primary"
                :disabled="!selectedBed"
                class="text-none"
                @click="confirmSelection"
            >
              <v-icon left>mdi-check</v-icon>
              Confirm Selection
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-expand-transition>
    </v-card-text>
    <loading-dialog :show="loading"></loading-dialog>
  </v-card>
</template>

<script>
import Api from "@/main/api";
import ErrorResolver from "@/shared/ErrorResolver";
import {formatCurrency} from "@/shared/CurrencyUtil";
import {mapGetters} from "vuex";
import LoadingDialog from "@ui/components/LoadingDialog.vue";

export default {
  name: 'EnhancedRoomSelection',
  components: {LoadingDialog},
  props: ['interest'],
  data: () => ({
    loading: false,
    selectedBlock: null,
    selectedFloor: null,
    selectedRoom: null,
    selectedBed: null,
    showHelpDialog: false,

    // Enhanced data structures
    blocks: [],
    floors: [],

    roomStatus: [
      { text: 'Available', color: 'success', icon: 'mdi-check-circle' },
      { text: 'Limited', color: 'warning', icon: 'mdi-alert' },
      { text: 'Full', color: 'error', icon: 'mdi-close-circle' },
      { text: 'Selected', color: 'primary', icon: 'mdi-star' }
    ],

    // Sample room data - replace with API data
    availableRooms: []
  }),

  computed: {
    selectedRoomDetails() {
      return this.availableRooms.find(room => room.id === this.selectedRoom);
    },

    filteredRooms() {
      if (!this.selectedBlock || !this.selectedFloor) return [];

      return this.availableRooms.filter(room => {
        return room.block === this.selectedBlock &&
            room.floor === this.selectedFloor;
      });
    },

    roomDetails() {
      if (!this.selectedRoomDetails) return [];

      return [
        {
          label: 'Block',
          value: this.selectedBlock?.name,
          icon: 'mdi-office-building'
        },
        {
          label: 'Room',
          value: this.selectedRoomDetails.name,
          icon: 'mdi-door'
        },
        {
          label: 'Room Type',
          value: this.selectedRoomDetails.type,
          icon: 'mdi-home'
        },
        {
          label: 'Selected SPace',
          value: this.selectedBed ? `${this.selectedBed.name}` : 'Not Selected',
          icon: 'mdi-bed'
        },
        {
          label: 'Price per Session',
          value: `${formatCurrency(this.selectedRoomDetails.price)}`,
          icon: 'mdi-currency-usd'
        },
        {
          label: 'Available Spaces',
          value: `${this.selectedRoomDetails.bedsAvailable}/${this.selectedRoomDetails.totalBeds}`,
          icon: 'mdi-account-group'
        }
      ];
    },

    canConfirm() {
      return this.selectedRoom && this.selectedBed && !this.loading;
    },
    ...mapGetters(['getUser'])
  },

  methods: {
    fetchBlocks(gender){
      Api.hostel.allBlocks(gender)
          .then(res => this.blocks = res.data)
          .catch(err => ErrorResolver.process(this, err))
          .finally(() => this.loading = false)
    },
    fetcFloor(){
      if(!this.selectedBlock)
        return;

      Api.hostel.floors(this.selectedBlock.id)
          .then(res => this.floors = res.data)
          .catch(err => ErrorResolver.process(this, err))
    },
    async fetchRooms() {
      this.loading = true;
      try {

        // Replace with actual API call
        const response = (await Api.hostel.roomsForSelection(this.interest.id, this.selectedFloor.id));

        this.availableRooms = response.data.map(room => ({
          ...room,
          totalBeds: room.capacity,
          bedsAvailable: room.spacesAvailable,
          number: room.name,
          showBedDialog: false,
          beds: room.spaces.map((space, i) => ({...space, type: 'Single', number: i + 1})),
          type: 'Regular Room'
        }));

      } catch (error) {
        await ErrorResolver.process(this, error)
      } finally {
        this.loading = false;
      }
    },

    async loadInformation(){
      this.loading = true
      Api.studentInformation.getInformation(this.getUser.userId)
          .then(res => {
            // console.log(res.data)
            if(res.data?.gender?.id){
              this.fetchBlocks(res.data.gender.id)
            } else{
              this.loading = false
              this.$notify({type: 'text', message: 'Please update your biodata'})
            }
          }).catch(err => {
        ErrorResolver.process(this, err)
        this.loading = false
      })
    },

    getRoomColor(room) {
      if (room.id === this.selectedRoom) return 'primary';
      if (!room.available) return 'error lighten-1';
      if (room.bedsAvailable === room.totalBeds) return 'success lighten-1';
      return 'warning lighten-1';
    },

    getRoomStatusClass(room) {
      if (room.id === this.selectedRoom) return 'status-selected';
      if (!room.available) return 'status-full';
      if (room.bedsAvailable === room.totalBeds) return 'status-available';
      return 'status-limited';
    },

    selectRoom(room) {
      if (!room.available) {
        this.$notify({
          type: 'warning',
          text: 'This room is not available for selection'
        });
        return;
      }

      this.selectedRoom = room.id;
      this.selectedBed = null;
      room.showBedDialog = true;

      // Scroll to room details if on mobile
      if (window.innerWidth < 600) {
        this.$nextTick(() => {
          this.$vuetify.goTo('.selected-room-details', {
            duration: 500,
            offset: 50,
            easing: 'easeInOutCubic'
          });
        });
      }
    },

    selectBed(room, bed) {
      if (!bed.available) {
        this.$notify({
          type: 'warning',
          text: 'This bed is not available for selection'
        });
        return;
      }

      this.selectedBed = bed;
      room.showBedDialog = false;

      this.$emit('selection', {
        block: this.selectedBlock,
        floor: this.selectedFloor,
        room: room.number,
        bed: bed.number,
        type: room.type,
        price: room.price
      });
    },

    async confirmSelection() {
      if (!this.canConfirm || !this.getUser) return;

      this.loading = true;
      try {
        // Replace with actual API call
        const alloc = await Api.hostel.selectSpace({
          space: this.selectedBed.id,
          studentId: this.interest.student.userId,
          sessionId: this.interest.session.id
        })

        window.location.reload();

        this.$emit('assigned', alloc);

        this.$notify({
          type: 'success',
          message: 'Room assigned successfully!'
        });

        // Reset selection after successful assignment
        this.resetSelection();
      } catch (error) {
        await ErrorResolver.process(this, error)
      } finally {
        this.loading = false;
      }
    },

    resetSelection() {
      this.selectedRoom = null;
      this.selectedBed = null;
      this.availableRooms.forEach(room => {
        room.showBedDialog = false;
      });
    },

    confirmBedSelection(room) {
      if (!this.selectedBed) return;
      room.showBedDialog = false;
    }
  },

  watch: {
    selectedBlock(newBlock) {
      if (newBlock) {
        this.resetSelection();
        this.fetcFloor();
      }
    },

    selectedFloor(newFloor) {
      if (newFloor && this.selectedBlock) {
        this.resetSelection();
        this.fetchRooms();
      }
    },

    getUser: function(val){
      if(val){
        this.loadInformation()
      }
    }
  },

  created() {
    // Initial room fetch if block and floor are pre-selected
    if (this.selectedBlock && this.selectedFloor) {
      this.fetchRooms();
    }
    if(this.getUser){
      this.loadInformation()
    }
  },

  beforeDestroy() {
    // Cleanup any open dialogs
    this.availableRooms.forEach(room => {
      room.showBedDialog = false;
    });
  }
};
</script>

<style scoped>
.room-selection-card {
  border-radius: 16px;
  overflow: hidden;
}

.selection-controls .v-text-field {
  border-radius: 12px;
}

.floor-plan {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  min-height: 400px;
  border-radius: 16px;
}

.room-card {
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.room-card.elevated {
  transform: translateY(-8px);
}

.room-card.selected {
  border: 3px solid var(--v-primary-base);
  transform: scale(1.05);
}

.room-status-indicator {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
}

.status-available {
  background-color: var(--v-success-base);
}

.status-limited {
  background-color: var(--v-warning-base);
}

.status-full {
  background-color: var(--v-error-base);
}

.bed-selection-dialog {
  border-radius: 16px;
  overflow: hidden;
}

.bed-card {
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.bed-card:hover:not([disabled]) {
  transform: translateY(-4px);
}

.bed-selected {
  border: 2px solid var(--v-primary-base);
  background-color: var(--v-primary-lighten5) !important;
}

.legend-card {
  border-radius: 12px;
  background-color: #fafafa;
}

.selected-room-details {
  border-radius: 16px;
  overflow: hidden;
}

@media (max-width: 600px) {
  .room-card {
    width: 130px;
  }

  .floor-plan {
    min-height: 350px;
  }
}
</style>
